"use client";

import React, { useState, useEffect, useRef } from "react";

const API_BASE = "/api";

type Job = {
  id: number;
  role: string;
  department: string;
  type: string;
  location: string;
  description: string | null;
  requirements: string | null;
  status: string;
};

type FormState = {
  full_name: string;
  email: string;
  phone: string;
  cover_letter: string;
};

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function Career() {
  const [jobs, setJobs]               = useState<Job[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [submitError, setSubmitError] = useState("");

  // File upload state
  const [resumeFile, setResumeFile]   = useState<File | null>(null);
  const [resumeError, setResumeError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<FormState>({
    full_name:    "",
    email:        "",
    phone:        "",
    cover_letter: "",
  });

  // ── Fetch active jobs from API
  useEffect(() => {
    fetch(`${API_BASE}/careers/jobs?status=active`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setJobs(data.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Reset form + status when modal closes
  const closeModal = () => {
    setSelectedJob(null);
    setSubmitStatus("idle");
    setSubmitError("");
    setResumeFile(null);
    setResumeError("");
    setForm({ full_name: "", email: "", phone: "", cover_letter: "" });
  };

  // ── Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setResumeError("");

    if (!file) {
      setResumeFile(null);
      return;
    }

    // Validate type: PDF, DOC, DOCX
    const allowed = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) {
      setResumeError("Only PDF, DOC, or DOCX files are accepted.");
      setResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Validate size: max 5MB
    if (file.size > 5 * 1024 * 1024) {
      setResumeError("File size must be under 5 MB.");
      setResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setResumeFile(file);
  };

  // ── Convert file to base64 data URL
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to read file."));
      reader.readAsDataURL(file);
    });

  // ── Submit application
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedJob) return;

    setSubmitStatus("loading");
    setSubmitError("");

    try {
      let resume_url: string | null = null;

      if (resumeFile) {
        resume_url = await fileToBase64(resumeFile);
      }

      const res = await fetch(`${API_BASE}/careers/apply`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_id:       selectedJob.id,
          job_role:     selectedJob.role,
          full_name:    form.full_name.trim(),
          email:        form.email.trim(),
          phone:        form.phone.trim() || null,
          cover_letter: form.cover_letter.trim() || null,
          resume_url,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);

      setSubmitStatus("success");
    } catch (err: any) {
      setSubmitError(err.message ?? "Something went wrong. Please try again.");
      setSubmitStatus("error");
    }
  };

  const f = (k: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // ── Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-white min-h-screen font-sans text-zinc-900">

      {/* ── HERO ── */}
      <section className="relative w-full h-[80vh] flex items-center justify-center overflow-hidden pt-20">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2074&auto=format&fit=crop"
          alt="Career at Modish Kreations"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="font-['Cormorant_Garamond'] italic text-6xl md:text-9xl font-light mb-6">
            Craft your future.
          </h1>
          <p className="font-['DM_Mono'] text-[11px] uppercase tracking-[0.4em] opacity-90">
            Join the team redefining boutique fashion.
          </p>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="pt-20 pb-20 px-6 border-b border-zinc-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#C7AB5F] font-bold">
            Work with us
          </span>
          <h1 className="mt-6 text-2xl md:text-6xl font-serif leading-tight italic">
            Join the <span className="not-italic">Modish Kreations.</span>
          </h1>
        </div>
      </section>

      {/* ── JOB LISTINGS ── */}
      <section className="py-10 px-6">
        <div className="max-w-7xl mx-auto">

          {loading && (
            <p className="py-16 text-center text-sm text-zinc-400 tracking-widest uppercase">
              Loading openings…
            </p>
          )}

          {!loading && error && (
            <p className="py-16 text-center text-sm text-red-400">
              Could not load jobs: {error}
            </p>
          )}

          {!loading && !error && jobs.length === 0 && (
            <p className="py-16 text-center text-sm text-zinc-400 tracking-widest uppercase">
              No openings at the moment. Check back soon.
            </p>
          )}

          {!loading && !error && (
            <div className="grid grid-cols-1 gap-0">
              {jobs.map((job, idx) => (
                <div
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  className="group border-b border-zinc-100 py-12 flex flex-col md:flex-row md:items-center justify-between hover:px-4 transition-all duration-500 ease-in-out cursor-pointer hover:bg-zinc-50/50"
                >
                  <div className="flex items-start gap-8 md:gap-16">
                    <span className="text-xs font-serif text-[#051173] group-hover:text-[#C7AB5F] transition-colors duration-300 mt-1">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="text-2xl md:text-3xl font-medium tracking-tight group-hover:translate-x-2 transition-transform duration-500">
                        {job.role}
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-4 text-[10px] uppercase tracking-widest text-[#051173]">
                        <span>{job.department}</span>
                        <span>•</span>
                        <span>{job.type}</span>
                        <span>•</span>
                        <span>{job.location}</span>
                      </div>
                      {job.description && (
                        <p className="mt-3 text-xs text-zinc-400 max-w-xl leading-relaxed line-clamp-2">
                          {job.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 md:mt-0 overflow-hidden flex-shrink-0">
                    <button className="relative group/btn flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] overflow-hidden">
                      <span className="inline-block transition-transform duration-500 group-hover/btn:-translate-y-[120%]">
                        Apply Now
                      </span>
                      <span className="absolute inset-0 inline-block translate-y-[120%] transition-transform duration-500 group-hover/btn:translate-y-0 text-[#C7AB5F]">
                        Apply Now
                      </span>
                      <span className="text-lg">→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── APPLICATION MODAL ── */}
      {selectedJob && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bg-white p-10 max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-xs uppercase hover:text-[#C7AB5F] transition-colors"
            >
              Close ✕
            </button>

            {/* ── Success state ── */}
            {submitStatus === "success" ? (
              <div className="py-8 text-center space-y-4">
                <div className="text-4xl">✓</div>
                <h2 className="text-2xl font-serif italic">Application Received</h2>
                <p className="text-sm text-zinc-500">
                  Thank you for applying for <strong>{selectedJob.role}</strong>. We'll be in touch soon.
                </p>
                <button
                  onClick={closeModal}
                  className="mt-4 px-8 py-3 bg-[#051173] text-white text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#C7AB5F] transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-serif italic mb-1">
                  Apply for {selectedJob.role}
                </h2>
                <p className="text-[10px] uppercase tracking-widest text-[#051173] mb-8">
                  {selectedJob.department} · {selectedJob.type} · {selectedJob.location}
                </p>

                {selectedJob.description && (
                  <p className="text-xs text-zinc-500 mb-6 leading-relaxed border-l-2 border-[#C7AB5F] pl-4">
                    {selectedJob.description}
                  </p>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={form.full_name}
                      onChange={f("full_name")}
                      placeholder="Your full name"
                      className="w-full border-b border-zinc-200 py-2 outline-none text-sm focus:border-zinc-900 transition-colors bg-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={f("email")}
                      placeholder="you@example.com"
                      className="w-full border-b border-zinc-200 py-2 outline-none text-sm focus:border-zinc-900 transition-colors bg-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={f("phone")}
                      placeholder="+91 98765 43210"
                      className="w-full border-b border-zinc-200 py-2 outline-none text-sm focus:border-zinc-900 transition-colors bg-transparent"
                    />
                  </div>

                  {/* ── Resume Upload ── */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
                      Resume / CV
                    </label>

                    {/* Drop zone */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`
                        relative cursor-pointer border-2 border-dashed rounded-sm px-5 py-6 text-center
                        transition-colors duration-300
                        ${resumeFile
                          ? "border-[#C7AB5F] bg-amber-50/40"
                          : "border-zinc-200 hover:border-zinc-400 bg-zinc-50/50"
                        }
                      `}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileChange}
                        className="hidden"
                      />

                      {resumeFile ? (
                        /* File selected */
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {/* File icon */}
                            <div className="flex-shrink-0 w-9 h-9 bg-[#051173] text-white flex items-center justify-center rounded-sm text-[9px] font-bold uppercase">
                              {resumeFile.name.split(".").pop()}
                            </div>
                            <div className="text-left min-w-0">
                              <p className="text-xs font-medium text-zinc-800 truncate max-w-[180px]">
                                {resumeFile.name}
                              </p>
                              <p className="text-[10px] text-zinc-400 mt-0.5">
                                {formatSize(resumeFile.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setResumeFile(null);
                              setResumeError("");
                              if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                            className="flex-shrink-0 text-zinc-400 hover:text-red-500 transition-colors text-lg leading-none"
                            aria-label="Remove file"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        /* Empty state */
                        <div className="space-y-2">
                          <div className="text-2xl text-zinc-300">↑</div>
                          <p className="text-xs text-zinc-500">
                            <span className="font-medium text-zinc-700">Click to upload</span> or drag &amp; drop
                          </p>
                          <p className="text-[10px] text-zinc-400">
                            PDF, DOC, or DOCX · Max 5 MB
                          </p>
                        </div>
                      )}
                    </div>

                    {resumeError && (
                      <p className="text-[10px] text-red-500 mt-1">{resumeError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                      Cover Letter
                    </label>
                    <textarea
                      rows={4}
                      value={form.cover_letter}
                      onChange={f("cover_letter")}
                      placeholder="Tell us why you're the perfect fit…"
                      className="w-full border-b border-zinc-200 py-2 outline-none text-sm focus:border-zinc-900 transition-colors bg-transparent resize-none"
                    />
                  </div>

                  {submitStatus === "error" && (
                    <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded">
                      {submitError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === "loading"}
                    className="w-full mt-2 bg-[#051173] text-white py-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#C7AB5F] transition-colors duration-300 disabled:opacity-50"
                  >
                    {submitStatus === "loading" ? "Submitting…" : "Submit Application"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── SPONTANEOUS ── */}
      <section className="py-12 px-6 bg-zinc-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif italic mb-6">Don't see your role?</h2>
          <p className="text-zinc-500 mb-10 max-w-md mx-auto">
            We're always looking for exceptional talent. Send your portfolio and CV to our design head.
          </p>
          <a
            href="mailto:info@modishkreations.com"
            className="inline-block px-12 py-5 bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-[#C7AB5F] transition-colors duration-500 rounded-full"
          >
            Spontaneous Application
          </a>
        </div>
      </section>
    </div>
  );
}